import React, { useEffect, useRef, useState, MouseEvent } from 'react';
import {
  MdFilterAlt,
  MdOutlineFileDownload,
  MdOutlineSort,
  MdRefresh,
  MdSettings,
} from 'react-icons/md';

import { QueryParams } from '@dinnisoft/esteio-gestao-common';
import MaterialTable, { Column } from '@material-table/core';
// import { ExportCsv, ExportPdf } from '@material-table/exporters';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import FileSaver from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { TableProps } from 'types/table';
import { unmaskValues } from 'utils/unmaskValues';

import { Elevation, IconContainer, NoElevation, OptionButton, Title } from './styles';

export default function Table(props: TableProps) {
  const [all, setAll] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [showGroup, setshowGroup] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [errorOnLoad, setErrorOnLoad] = useState(false);
  const open = Boolean(anchorEl);
  const tableRef = useRef<any>(null);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const filterClick = () => {
    handleClose();
    setShowFilter(!showFilter);
  };

  const groupClick = () => {
    handleClose();
    setshowGroup(!showGroup);
  };

  useEffect(() => {
    if (typeof props.data === 'string') {
      tableRef.current && tableRef.current.onQueryChange();
    }
  }, [props.data, props.refresh]);

  const formatDateField = (cellValue: any, fieldName: any) => {
    if (fieldName.includes('data')) {
      let dateValue = cellValue;

      if (typeof cellValue === 'string') {
        dateValue = parseISO(cellValue);
      }

      if (dateValue instanceof Date) {
        return format(dateValue, 'dd/MM/yyyy');
      }
    }

    return cellValue;
  };

  const ExportIcon = React.forwardRef((props, _) => (
    <IconContainer className="test">
      <MdOutlineFileDownload fontSize={20} {...props} />
      Exportar
    </IconContainer>
  ));
  ExportIcon.displayName = 'ExportIcon';

  return (
    <Elevation>
      <OptionButton>
        <Title variant="h6" gutterBottom>
          {props.title}
        </Title>
        <IconButton
          id="basic-button"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          size="medium"
        >
          <MdSettings />
        </IconButton>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem selected={showFilter} onClick={filterClick}>
            <ListItemIcon>
              <MdFilterAlt />
            </ListItemIcon>
            <ListItemText>{showFilter ? 'Esconder' : 'Mostrar'} Filtro</ListItemText>
          </MenuItem>
          <MenuItem selected={showGroup} onClick={groupClick}>
            <ListItemIcon>
              <MdOutlineSort />
            </ListItemIcon>
            <ListItemText>{showGroup ? 'Esconder' : 'Mostrar'} Agrupamento</ListItemText>
          </MenuItem>
          {typeof props.data === 'string' ? (
            <MenuItem onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
              <ListItemIcon>
                <MdRefresh />
              </ListItemIcon>
              <ListItemText>Atualizar Tabela</ListItemText>
            </MenuItem>
          ) : null}
        </Menu>
      </OptionButton>

      <NoElevation>
        <MaterialTable
          title=""
          columns={props.columns.map((column) => {
            return {
              ...column,
              cellStyle: {
                ...(column.cellStyle || {}),
                width: 'auto',
                whiteSpace: 'nowrap',
              },
              tooltip: column.tooltip,
            };
          })}
          isLoading={props.isLoading}
          actions={props.actions}
          tableRef={tableRef}
          detailPanel={props?.detailPanel}
          icons={{
            Export: ExportIcon,
          }}
          options={{
            ...props.options,
            debounceInterval: 500,
            padding: 'dense',
            filtering: showFilter,
            grouping: showGroup,
            exportAllData: true,
            pageSizeOptions:
              typeof props.data === 'string'
                ? [5, 10, 20, 50, 100, all].filter((x) => all >= x).map((x) => x)
                : [5, 10, 20, 50],
            paginationType: 'stepped',
            numberOfPagesAround: 3,
            emptyRowsWhenPaging: false,
            columnResizable: true,
            headerStyle: {
              paddingLeft: '15px',
              textAlign: 'initial', // Alinha os cabeçalhos à esquerda
            },
            rowStyle: (rowData) => ({
              backgroundColor: rowData.clicked ? 'transparent' : 'initial', // Altere as cores conforme necessário
            }),
            hideFilterIcons: true,
            exportMenu: [
              // NOTA: consideramos que cada col.title possa ser corretamente convertido para uma string
              // e que data seja uma matriz de objetos com valores que possam ser
              // convertidos para uma string ou número, que são os tipos de dados que o
              // jspdf-autotable e XLSX.utils.json_to_sheet podem lidar.
              {
                label: 'Exportar para PDF',
                exportFunc: (cols, data) => {
                  // Define um novo documento PDF
                  const doc = new jsPDF({ orientation: 'landscape' });

                  // Mapeia os dados e as colunas para o formato necessário pelo autoTable
                  const pdfData = data.map((row) =>
                    cols.map((col) =>
                      formatDateField(row[col.field as keyof typeof row], col.field),
                    ),
                  );
                  const pdfCols = cols.map((col) => col.title as string);

                  // Adiciona a tabela ao documento
                  autoTable(doc, {
                    head: [pdfCols],
                    body: pdfData,
                    styles: { fontSize: 7, cellPadding: 1, overflow: 'linebreak' },
                    columnStyles: { 0: { cellWidth: 'wrap', minCellWidth: 80 } }, // Define a largura da primeira coluna para 'wrap'
                    theme: 'grid',
                  });

                  // Gera um download
                  doc.save(`${props.title}_${new Date().toLocaleDateString()}_pdf.pdf`);
                },
              },
              {
                label: 'Exportar para CSV',
                exportFunc: (cols, data) => {
                  const columnTitles = cols.map((col) => col.title);

                  const csvRows = data.map((row) => {
                    return cols
                      .map((col) => formatDateField(row[col.field as keyof typeof row], col.field))
                      .join(';');
                  });

                  const csvContent = [columnTitles.join(';'), ...csvRows].join('\n');
                  const BOM = '\uFEFF';
                  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

                  FileSaver.saveAs(
                    blob,
                    `${props.title}_${new Date().toLocaleDateString()}_csv.csv`,
                  );
                },
              },
            ],
          }}
          data={
            typeof props.data === 'string'
              ? (query) =>
                  new Promise((resolve) => {
                    (async () => {
                      let filters = '';
                      const search = unmaskValues(query.search);

                      if (query.filters.length > 0) {
                        query.filters.map((filter) =>
                          String(
                            (filters += `${filter.column.field} LIKE '%${unmaskValues(
                              filter.value,
                            )}%' &`),
                          ),
                        );
                      }

                      let dataResult = [];
                      let countResult = 0;
                      setErrorOnLoad(false);

                      const sortedColumns = props.columns.filter((col) => col.defaultSort);

                      let defaultOrderBy = '';
                      let defaultOrderDirection = '';

                      if (sortedColumns.length > 0) {
                        defaultOrderBy = sortedColumns[0].field;
                        defaultOrderDirection = sortedColumns[0].defaultSort ?? 'asc';
                      }

                      try {
                        const queryParams: QueryParams = {
                          page: query.page,
                          limit: query.pageSize,
                          search: search,
                          columns: props.columns
                            .map((column: Column<object>) => column.field)
                            .filter((column: string | string[] | undefined) => {
                              if (column && column.length > 0) return column;
                            })
                            .toString(),
                          orderBy: query.orderBy ? query.orderBy.field?.toString() : defaultOrderBy,
                          orderDirection: query.orderDirection ?? defaultOrderDirection,
                          filters: filters,
                        };
                        const result = await axios.get(String(props.data), {
                          params: queryParams,
                        });

                        dataResult = result.data.data;
                        countResult = result.data.count;
                        console.log(countResult);
                      } catch (error) {
                        dataResult = [];
                        countResult = 0;
                        setErrorOnLoad(true);
                      }

                      setAll(countResult);

                      resolve({
                        data: dataResult,
                        page: query.page,
                        totalCount: countResult,
                      });
                    })();
                  })
              : props.data
          }
          localization={{
            body: {
              emptyDataSourceMessage: errorOnLoad
                ? 'Houve erro ao carregar os dados'
                : 'Nenhum registro para exibir',
              filterRow: {
                filterTooltip: 'Filtrar',
              },
            },
            header: {
              actions: 'Ações',
            },
            toolbar: {
              searchTooltip: 'Pesquisar',
              searchPlaceholder: 'Pesquisar',
              exportTitle: 'Exportar',
              exportAriaLabel: 'Exportar',
              exportPDFName: 'Exportar PDF',
              exportCSVName: 'Exportar CSV',
            },
            pagination: {
              labelRows: '',
              labelDisplayedRows: '{from}-{to} de {count}',
              labelRowsPerPage: 'Linhas por página:',
              firstTooltip: 'Primeira página',
              previousTooltip: 'Página anterior',
              nextTooltip: 'Próxima página',
              lastTooltip: 'Última página',
            },
            grouping: {
              placeholder: 'Arraste para agrupar',
            },
          }}
        />
      </NoElevation>
    </Elevation>
  );
}
